import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heading } from '@aaimplatform/ui';
import { useAuthStore } from '../stores/auth';

/**
 * OAuth callback handler page (N6 cookie cutover, SEC-V04).
 *
 * aaim-core's OAuth2 callback redirects here with a one-time, opaque code on
 * the query string (NOT tokens in the hash fragment). This page POSTs the code
 * to POST /api/v1/auth/exchange (credentials: 'include'); the backend consumes
 * the single-use code, sets the HttpOnly session cookies, and returns a body
 * with NO tokens. The page then hydrates the user from GET /api/v1/auth/me. The
 * SPA never receives a bearer token.
 *
 *   GET /auth/callback?code=<opaque>[&state=<...>]
 *
 * On error the authorization service redirects with ?error=<code>&message=<...>.
 *
 * NOTE: the aaim-core OAuth2 redirect that emits ?code= (vs the legacy hash
 * fragment) is the lead's atomic step; this reader is authored ahead of it so
 * aaim-core + all four institutional SPAs flip together. Do not browser-test
 * SSO until that flip lands. See .claude/rules/no-browser-token-storage.md.
 */
export function OAuthCallbackPage() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    const errorCode = params.get('error');
    if (errorCode) {
      setError(params.get('message') ?? 'Authentication failed. Please try again.');
      return;
    }

    const code = params.get('code');
    if (!code) {
      setError('No authentication code received. Please try again.');
      return;
    }

    void (async () => {
      try {
        // Exchange the one-time code for the HttpOnly session cookies. The
        // response body carries NO tokens; the session lives only in the cookie.
        const res = await fetch('/api/v1/auth/exchange', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ code }),
        });

        if (!res.ok) {
          setError('Sign-in could not be completed. Please try again.');
          return;
        }

        // Hydrate the user from the freshly-set cookie.
        const ok = await useAuthStore.getState().hydrateSession();
        if (!ok) {
          setError('Sign-in succeeded but the session could not be loaded. Please try again.');
          return;
        }

        // Clear stored OAuth state and strip the code from the URL/history.
        sessionStorage.removeItem('oauth_state');
        window.history.replaceState(null, '', '/auth/callback');

        navigate('/', { replace: true });
      } catch {
        setError('Failed to process authentication response.');
      }
    })();
  }, [navigate]);

  if (error) {
    return (
      <div className="min-h-screen bg-canvas flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-surface rounded shadow-sm border border-recessed p-8 text-center">
          <div className="h-1 bg-accent -mt-8 -mx-8 mb-6 rounded-t" />
          <Heading level={1} className="mb-4">
            Authentication Error
          </Heading>
          <p className="text-sm text-tertiary mb-6">{error}</p>
          <button
            type="button"
            onClick={() => navigate('/login', { replace: true })}
            className="px-6 py-2 bg-accent text-white text-sm font-medium rounded hover:bg-accent/90 transition-colors"
          >
            Return to login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-canvas flex items-center justify-center">
      <div className="text-center">
        <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-sm text-tertiary">Completing sign-in...</p>
      </div>
    </div>
  );
}
